import { useState, useEffect, useRef, useCallback } from "react";
import "./UsStyles.css";
import { generateReceiptHTML } from "../Esim/Edata.js";
import receiptLogo from "../../../../assets/PNG/RecieptLogo.png";
import maleInterviewer from "../../../../assets/Static/US-Interview/Male interviewer.png";
import femaleInterviewer from "../../../../assets/Static/US-Interview/Female  interviewer.png";
import {
  interviewQuestions,
  voiceConfig,
  improvementTips,
  howItWorksSteps,
  scoreCategories,
  evaluateAnswersWithAI,
  MODULE_CARDS,
  PAYMENT_OVERLAY_CONTENT,
  TERMS_AND_CONDITIONS_CONTENT,
  getQuestionsByModule,
  getModuleSets,
  REWARD_LABELS,
  REWARD_QUICK_BUTTONS,
  REWARD_LIMIT,
  REWARD_MESSAGES,
  PRICE_BREAKDOWN_LABELS,
  PHONE_CODE_OPTIONS,
  NO_ANSWER_MARKERS,
  NO_ANSWER_RECORDED,
  NO_ANSWER_PROVIDED,
  NO_ANSWER_WAS_PROVIDED,
  TIMER_DURATION,
  INTERVIEW_GREETINGS,
  buildIntroSpeech,
} from "./interviewData.js";
import configService from "../../../../services/configService";
import { ArrowLeft, Landmark, Mic, Clock, ShieldCheck, X, Check, CreditCard, Lock, Download, ArrowRight } from "lucide-react";
import PaymentForm, { PAYMENT_METHODS_LIST, PAYMENT_DETAILS_DEFAULT, validatePaymentDetails } from "../../../Common/PaymentForm/PaymentForm.jsx";
import { getWallet, saveWallet } from "../../Users/ProfileLogic.js";
import { useAuth } from "../../../../context/AuthContext";
import Login from "../../Users/Login";

// ─── AOS HOOK ──────────────────────────────────────────────────────────────────
function useAOS() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("AIaos-animate");
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );
    document.querySelectorAll("[data-aos]").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}

// ─── VOICE WAVE COMPONENT ─────────────────────────────────────────────────────
function VoiceWave({ active, color = "#4f8ef7" }) {
  return (
    <div className="AIvoice-wave">
      {[...Array(7)].map((_, i) => (
        <div
          key={i}
          className={`AIvoice-wave__bar${active ? " AIvoice-wave__bar--active" : ""}`}
          style={{
            "--wave-color": color,
            "--wave-h": active ? `${16 + Math.sin(i) * 14}px` : "6px",
            "--wave-anim": active
              ? `AIwave ${0.8 + i * 0.1}s ease-in-out infinite alternate`
              : "none",
          }}
        />
      ))}
    </div>
  );
}

// ─── SCORE RING COMPONENT ─────────────────────────────────────────────────────
function ScoreRing({ score, label, color }) {
  const r = 24;
  const circ = 2 * Math.PI * r;
  const dash = (score / 100) * circ;
  return (
    <div className="AIscore-ring">
      <svg width="56" height="56" viewBox="0 0 56 56">
        <circle cx="28" cy="28" r={r} fill="none" stroke="rgba(13,27,62,0.08)" strokeWidth="5" />
        <circle
          cx="28" cy="28" r={r}
          fill="none"
          stroke={color}
          strokeWidth="5"
          strokeLinecap="round"
          strokeDasharray={`${dash} ${circ - dash}`}
          strokeDashoffset={circ / 4}
          className="AIscore-ring__stroke"
        />
        <text x="28" y="32" textAnchor="middle" fill="var(--AI-navy)" fontSize="12" fontWeight="700" fontFamily="'DM Sans', sans-serif">
          {score}%
        </text>
      </svg>
      <span className="AIscore-ring__label">{label}</span>
    </div>
  );
}

// ─── TERMS MODAL ──────────────────────────────────────────────────────────────
function TermsModal({ onClose }) {
  return (
    <div className="AIterms-overlay" onClick={onClose}>
      <div className="AIterms-box AIglass-strong" onClick={(e) => e.stopPropagation()}>
        <div className="AIterms-header">
          <h3 className="AIterms-header__title">{TERMS_AND_CONDITIONS_CONTENT.title}</h3>
          <button className="AIterms-close-btn" onClick={onClose}>
            <X size={18} strokeWidth={2} />
          </button>
        </div>

        <div className="AIterms-body">
          {TERMS_AND_CONDITIONS_CONTENT.sections.map((section, i) => (
            <div key={i} className="AIterms-section">
              <h4 className="AIterms-section__heading">{section.heading}</h4>
              <p className="AIterms-section__body">{section.body}</p>
            </div>
          ))}
        </div>

        {/* <div className="AIterms-footer">
          <button className="AIbtn AIbtn-primary AIbtn-full" onClick={onClose}>
            I Understand
          </button>
        </div> */}
      </div>
    </div>
  );
}

// ─── MODULE SELECTION SCREEN ──────────────────────────────────────────────────
function ModuleSelectionScreen({ selectedModule, onSelectModule, onBack, onContinue, onHome }) {
  useAOS();

  return (
    <div className="AIpage AIpage-center">
      <div className="AIorb AIorb-1" />
      <div className="AIorb AIorb-2" />

      <div className="AIsetup-wrapper AImodules-wrapper">
        <div className="AIsetup-card AIglass-strong AImodules-card">
          <button className="AIsetup-back-btn" onClick={onHome}>
            <ArrowLeft size={14} strokeWidth={2.5} />
            Back 
          </button>

          <div className="AIsetup-card__header">
            <h2 className="AIsetup-card__h2">Choose Your Interview Module</h2>
          </div>

          <div className="AIpayment-plans-grid AImodules-grid">
            {MODULE_CARDS.map((mod, i) => {
              const isSelected = selectedModule === mod.id;
              const cardClasses = [
                "AIpayment-plan-card",
                "AIglass-strong",
                "AImodule-card",
                isSelected ? "AImodule-card--selected" : "",
                mod.locked ? "AImodule-card--locked" : "",
              ].filter(Boolean).join(" ");
              return (
                <div
                  key={mod.id}
                  className={cardClasses}
                  // data-aos="fade-up"
                  // data-aos-delay={`${i * 100}`}
                  style={{ "--module-color": mod.color }}
                  onClick={() => !mod.locked && onSelectModule(mod.id)}
                >
                  {/* {mod.badge && (
                    <div
                      className={`AIpayment-plan-card__badge ${mod.locked ? "AImodule-card__badge--locked" : ""}`}
                    >
                      {mod.locked ? "🔒 Locked" : mod.badge}
                    </div>
                  )} */}

                  <div className="AImodule-card__icon">{mod.icon}</div>
                  <div className="AIpayment-plan-card__name">{mod.name}</div>
                  <div className="AImodule-card__label">
                    {mod.label}
                  </div>

                  <div className="AIpayment-plan-card__price">
                    {mod.price > 0 ? (
                      <>
                        <span className="AIpayment-plan-card__currency">₹</span>
                        <span className="AIpayment-plan-card__amount">{mod.priceWithGST}</span>
                        <span className="AIpayment-plan-card__period AIgst-label">incl GST {mod.gstPercent}%</span>
                      </>
                    ) : (
                      <span className="AIpayment-plan-card__amount AImodule-card__free-price">
                        Free
                      </span>
                    )}
                  </div>

                  <ul className="AIpayment-plan-card__features">
                    {mod.features.map((f, fi) => (
                      <li key={fi} className="AIpayment-plan-card__feature">
                        <Check size={13} strokeWidth={2.5} color={mod.locked ? "rgba(13,27,62,0.2)" : mod.color} />
                        {f}
                      </li>
                    ))}
                  </ul>

                  {mod.locked ? (
                    <button
                      className="AIbtn AIbtn-ghost AIbtn-full AImodule-card__btn AImodule-card__btn--locked"
                      disabled
                    >
                      🔒 Coming Soon
                    </button>
                  ) : (
                    <button
                      className={`AIbtn AIbtn-primary AIbtn-full AImodule-card__btn ${isSelected ? "AImodule-card__btn--selected" : ""}`}
                    >
                      {isSelected ? "✓ Selected" : "Select Module"}
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          <button
            className={`AIbtn AIbtn-primary AIbtn--begin ${!selectedModule ? "AIbtn--disabled" : ""}`}
            onClick={selectedModule ? onContinue : undefined}
            disabled={!selectedModule}
          >
            Continue with {selectedModule ? MODULE_CARDS.find((m) => m.id === selectedModule)?.name : "Module"} 
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── LANDING SCREEN ───────────────────────────────────────────────────────────
function LandingScreen({ onStart }) {
  useAOS();
  return (
    <div className="AIpage">
      <div className="AIorb AIorb-1" />
      <div className="AIorb AIorb-2" />
      <div className="AIorb AIorb-3" />

      <div className="AIlanding__container">
        <div className="AIlanding__hero">
          <h1 data-aos="fade-up" data-aos-delay="100" className="AIlanding__h1">
            Have an Interview with AI
          </h1>
          <div data-aos="fade-up" data-aos-delay="300" className="AIlanding__cta">
            <button className="AIbtn AIbtn-primary" onClick={onStart}>
              Start Free Interview
            </button>
          </div>
        </div>

        <div className="AIsection">
          <div data-aos="fade-up" className="AIsection__header"></div>
          <div className="AIsteps-grid">
            {howItWorksSteps.map((item, i) => (
              <div key={i} className="AIstep-card AIglass-strong" data-aos="fade-up" data-aos-delay={`${i * 150}`}>
                <div className="AIstep-card__bg-num">{item.step}</div>
                {/* <div className="AIstep-card__icon-wrap">{item.icon}</div> */}
                <div className="AIstep-card__num">Step {item.step}</div>
                <div className="AIstep-card__title">{item.title}</div>
                <div className="AIstep-card__desc">{item.desc}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="AIsections">
          <div data-aos="fade-up" className="AIsection__header">
            <h2 className="AIsection__h2">Sample Interview Questions</h2>
            <p className="AIsection__sub">Real questions asked at US visa interviews</p>
          </div>
          <div className="AIquestions-grid">
            {interviewQuestions.slice(0, 6).map((q, i) => (
              <div key={q.id} className="AIquestion-preview-card AIglass" data-aos="fade-up" data-aos-delay={`${(i % 3) * 100}`}>
                <div className="AIquestion-preview-card__num">{q.id}</div>
                <div>
                  <div className="AIquestion-preview-card__cat">{q.category}</div>
                  <div className="AIquestion-preview-card__text">{q.question}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <footer className="AIfooter">
        <p className="AIfooter__text">US Visa Mock Interview • AI-Powered Preparation Tool • The Visa Co</p>
      </footer>
    </div>
  );
}

// ─── SETUP SCREEN ─────────────────────────────────────────────────────────────
function SetupScreen({ selectedModule, attemptsUsed, selectedSet, onSelectSet, selectedVoice, onSelectVoice, onBack, onBegin }) {
  const mod = MODULE_CARDS.find((m) => m.id === selectedModule);
  const remaining = mod.maxAttempts - attemptsUsed;
  const moduleSets = getModuleSets(selectedModule);
  const setNames = mod.sets;
  return (
    <div className="AIpage AIpage-center">
      <div className="AIorb AIorb-1" />
      <div className="AIorb AIorb-2" />

      <div className="AIsetup-wrapper" style={{ "--module-color": mod.color, "--module-color-bg": mod.colorBg, "--module-color-border": mod.colorBorder }}>
        <div className="AIsetup-card AIglass-strong">
          <button className="AIsetup-back-btn" onClick={onBack}>
            <ArrowLeft size={14} strokeWidth={2.5} />
            Back
          </button>

          <div className="AIsetup-card__header">
            {/* <div className="AIsetup-card__icon-wrap">
              <Landmark size={28} color="#818cf8" strokeWidth={1.8} />
            </div> */}
            <h2 className="AIsetup-card__h2">Let's Get Started</h2>
            {/* <p className="AIsetup-card__sub">Choose between our male and female AI Consular Officers</p> */}
          </div>

          <div className="AIsetup-horizontal-layout">
            <div className="AIsetup-horizontal-section">
              <div className="AIsetup-section-header">
                <h3 className="AIsetup-section-title">
                  Select Interview Set
                </h3>
              </div>
              <div className="AIvoice-cards-grid AIonset-grid">
                {setNames.map((setName, idx) => {
                  const setKey = `Set ${idx + 1}`;
                  const isSelected = selectedSet === setName;
                  const icons = ["📋", "📝", "📄", "📑", "📒"];
                  return (
                    <div
                      key={setName}
                      className={`AIvoice-card ${isSelected ? "AIselected" : ""}`}
                      onClick={() => onSelectSet(setName)}
                    >
                      <div className="AIvoice-card__name">{setKey}</div>
                      <div className="AIvoice-card__badge">{mod.questionsPerSet} Questions</div>
                      {isSelected && (
                        <div className="AIvoice-card__selected-pill">
                          <span className="AIvoice-card__selected-dot" />
                          <span className="AIvoice-card__selected-text">Selected</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="AIsetup-info-bar AIglass">
                <div className="AIsetup-info-item">
                  <div className="AIsetup-info-item__icon-wrap">
                    <ShieldCheck size={14} color="#22c55e" strokeWidth={2} />
                  </div>
                  <span className={`AIsetup-info-item__text AIsetup-attempts-text ${remaining <= 1 ? "AIsetup-attempts-text--low" : ""}`}>
                    {remaining} attempt{remaining !== 1 ? "s" : ""} remaining
                  </span>
                </div>
                <div className="AIsetup-info-item">
                  <div className="AIsetup-info-item__icon-wrap">
                    <Clock size={14} color="#818cf8" strokeWidth={2} />
                  </div>
                  <span className="AIsetup-info-item__text">~15 min session</span>
                </div>
                <div className="AIsetup-info-item">
                  <div className="AIsetup-info-item__icon-wrap">
                    <Mic size={14} color="#818cf8" strokeWidth={2} />
                  </div>
                  <span className="AIsetup-info-item__text">Voice powered</span>
                </div>
              </div>

              <button className="AIbtn AIbtn-primary AIbtn-full AIbtn--begin" onClick={onBegin}>
               Begin Interview
              </button>
            </div>

            <div className="AIsetup-horizontal-section">
              <div className="AIsetup-section-header">
                <h3 className="AIsetup-section-title">
                  Select Officer
                </h3>
              </div>
              <div className="AIvoice-cards-grid">
                {Object.entries(voiceConfig).map(([key, cfg]) => (
                  <div
                    key={key}
                    className={`AIvoice-card ${selectedVoice === key ? "AIselected" : ""}`}
                    onClick={() => onSelectVoice(key)}
                  >
                    <div className="AIvoice-card__avatar AIvoice-card__photo">
                      <img src={key === "male" ? maleInterviewer : femaleInterviewer} alt={cfg.name} />
                    </div>
                    <div className="AIvoice-card__name">{cfg.name}</div>
                    <div className="AIvoice-card__badge">{cfg.badge.toUpperCase()}</div>
                    {selectedVoice === key && (
                      <div className="AIvoice-card__selected-pill">
                        <span className="AIvoice-card__selected-dot" />
                        <span className="AIvoice-card__selected-text">Selected</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <p className="AIsetup-note">
            <Mic size={11} color="rgba(255,255,255,0.25)" />
            Ensure your microphone is enabled for best experience
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── INTERVIEW SCREEN ─────────────────────────────────────────────────────────
function InterviewScreen({ selectedModule, selectedVoice, currentQ, isAISpeaking, isListening, transcript, timer, questionPhase, onNext, onBack, activeQuestions }) {
  const mod = MODULE_CARDS.find((m) => m.id === selectedModule);
  const progress = (currentQ / activeQuestions.length) * 100;
  const question = activeQuestions[currentQ];

  return (
    <div className="AIpage AIpage-center AIpage-center--col">
      <div className="AIorb AIorb-1" />
      <div className="AIorb AIorb-2" />

      <div className="AIinterview-wrapper">
        <button className="AIinterview-back-btn" onClick={onBack}>
          <ArrowLeft size={14} strokeWidth={2.5} />
          Back
        </button>

        <div className="AIinterview-header">
          <div className="AIinterview-header__left">
            {/* <div className="AIinterview-header__flag">🇺🇸</div> */}
            <div>
              <div className="AIinterview-header__title">US Embassy Mock Interview</div>
              <div className="AIinterview-header__sub">{mod?.label} Module</div>
            </div>
          </div>
          <div className="AIinterview-header__counter AIglass">
            <span className="AIinterview-header__counter-label">Question</span>
            <span className="AIinterview-header__counter-num">{Math.min(currentQ + 1, activeQuestions.length)}</span>
            <span className="AIinterview-header__counter-sep">/</span>
            <span className="AIinterview-header__counter-total">{activeQuestions.length}</span>
          </div>
        </div>

        <div className="AIprogress-bar">
          <div className="AIprogress-bar__fill" style={{ "--ai-progress": `${progress}%` }} />
        </div>

        <div className="AIinterview-panels">
          <div className="AIofficer-panel AIglass-strong">
            <div className="AIofficer-panel__identity">
              <div className={`AIofficer-panel__avatar AIofficer-panel__photo ${isAISpeaking ? "AIspeaking" : "AIidle"}`}>
                <img src={selectedVoice === "male" ? maleInterviewer : femaleInterviewer} alt={voiceConfig[selectedVoice].name} loading="lazy" />
              </div>
              <div className="AIofficer-panel__info">
                <div className="AIofficer-panel__name">{voiceConfig[selectedVoice].name}</div>
                <div className="AIofficer-panel__status">
                  <span className={`AIofficer-panel__status-dot ${isAISpeaking ? "AIofficer-panel__status-dot--speaking" : "AIofficer-panel__status-dot--ready"}`} />
                  <span className="AIofficer-panel__status-text">
                    {isAISpeaking ? "Speaking..." : questionPhase === "listening" ? "Listening to you" : "Ready"}
                  </span>
                </div>
              </div>
            </div>

            <VoiceWave active={isAISpeaking} color="#4f8ef7" />

            <div className="AIofficer-panel__question-wrap">
              <div className="AIofficer-panel__category">{question?.category}</div>
              <p className="AIofficer-panel__question">{question?.question}</p>
              <div className="AIofficer-panel__hint">
                <span className="AIofficer-panel__hint-icon">💡</span>
                <span className="AIofficer-panel__hint-text">{question?.hint}</span>
              </div>
            </div>
          </div>

          <div className="AIresponse-panel AIglass-strong">
            <div className="AIresponse-panel__header">
              <div className="AIresponse-panel__mic-wrap">
                <div className={`AIresponse-panel__mic-btn ${isListening ? "AIlistening" : "AIidle"}`}>🎙️</div>
                <span className={`AIresponse-panel__mic-label ${isListening ? "AIlistening" : "AIidle"}`}>
                  {isListening ? "Recording your answer..." : questionPhase === "speaking" ? "Please wait..." : "Microphone standby"}
                </span>
              </div>

              {questionPhase === "listening" && (
                <div className="AIresponse-panel__timer">
                  <span className={`AIresponse-panel__timer-icon ${timer <= 10 ? "AIresponse-panel__timer-icon--danger" : "AIresponse-panel__timer-icon--normal"}`}>⏱</span>
                  <span className={`AIresponse-panel__timer-count ${timer <= 10 ? "AIdanger" : "AInormal"}`}>{timer}s</span>
                </div>
              )}
            </div>

            <VoiceWave active={isListening} color="#22c55e" />

            <div className="AIresponse-panel__transcript">
              {transcript ? (
                <p className="AIresponse-panel__transcript-text">{transcript}</p>
              ) : (
                <p className="AIresponse-panel__transcript-placeholder">
                  {questionPhase === "listening"
                    ? "Speak now — your answer will appear here..."
                    : "Waiting for the question to finish..."}
                </p>
              )}
            </div>

            {questionPhase === "listening" && (
              <button className="AIbtn AIbtn-primary AIbtn-full AIbtn--submit-mt" onClick={onNext}>
                Submit Answer & Continue 
              </button>
            )}
          </div>
        </div>

        {/* <div className="AIquestion-dots">
          {activeQuestions.map((_, i) => (
            <div key={i} className={`AIquestion-dot ${i < currentQ ? "AIdone" : i === currentQ ? "AIcurrent" : "AIpending"}`} />
          ))}
        </div> */}
      </div>
    </div>
  );
}

// ─── ANALYZING SCREEN ─────────────────────────────────────────────────────────
function AnalyzingScreen() {
  return (
    <div className="AIpage">
      <div className="AIorb AIorb-1" />
      <div className="AIorb AIorb-2" />
      <div className="AIanalyzing-wrapper">
        <div className="AIanalyzing-spinner" />
        <div className="AIanalyzing-title">Analyzing Your Interview</div>
        <div className="AIanalyzing-sub">
          Our AI is generating ideal answers and evaluating your responses. This takes a few seconds…
        </div>
        <div className="AIanalyzing-shimmer" />
      </div>
    </div>
  );
}

// ─── LOGIN GATE ──────────────────────────────────────────────────────────
function LoginGate({ open, onClose, onLoginSuccess }) {
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  if (!open) return null;

  return (
    <div className="AI-lg-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="AI-lg-inner">
        <button className="AI-lg-close" onClick={onClose}>✕</button>
        <Login onLoginSuccess={onLoginSuccess} />
      </div>
    </div>
  );
}

// ─── PAYMENT OVERLAY ──────────────────────────────────────────────────────────
function PaymentOverlay({ module, onPaymentSuccess, onClose }) {
  const [loading, setLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [details, setDetails] = useState({ ...PAYMENT_DETAILS_DEFAULT });
  const [error, setError] = useState("");
  const [rewardAmountInput, setRewardAmountInput] = useState("");
  const [useRewardDiscount, setUseRewardDiscount] = useState(false);
  const [showLoginGate, setShowLoginGate] = useState(false);
  const [showSuccessCard, setShowSuccessCard] = useState(false);
  const [transactionDetails, setTransactionDetails] = useState(null);
  const [billingName, setBillingName] = useState({ first: "", last: "" });
  const [billingEmail, setBillingEmail] = useState("");
  const [billingPhone, setBillingPhone] = useState("");
  const [billingCountry, setBillingCountry] = useState("");
  const [phoneCode, setPhoneCode] = useState("+91");

  const { user: session } = useAuth();
  const userEmail = session?.email || "";
  const wallet = getWallet(userEmail);
  const redeemableBalance = wallet?.redeemable?.balance ?? 0;
  const nonRedeemableBalance = wallet?.nonRedeemable?.balance ?? 0;
  const totalBalance = redeemableBalance + nonRedeemableBalance;

  // Custom round: > 0.5 → up, < 0.5 → down
  const roundReward = (val) => {
    const floor = Math.floor(val);
    const frac = val - floor;
    return frac > 0.5 ? Math.ceil(val) : floor;
  };

  const roundedRedeemable = roundReward(redeemableBalance);
  const roundedNonRedeemable = roundReward(nonRedeemableBalance);
  const roundedTotal = roundReward(totalBalance);
  const price = module.priceWithGST;

  const isRewardMethod = selectedMethod === "reward";
  const hasEnoughBalance = roundedTotal >= price;
  const maxRewardFromLimit = Math.floor(module.price * (REWARD_LIMIT.maxDiscountPercent / 100));
  const maxRewardAllowed = Math.min(maxRewardFromLimit, roundedTotal);

  const rewardAmount = isRewardMethod
    ? price
    : (rewardAmountInput ? parseInt(rewardAmountInput.replace(/\D/g, ""), 10) || 0 : 0);
  const remainingToPay = Math.max(0, module.priceWithGST - rewardAmount);
  const rewardExceedsBalance = isRewardMethod ? price > roundedTotal : rewardAmount > roundedTotal;
  const rewardExceedsPrice = isRewardMethod ? false : rewardAmount > price;
  const rewardExceedsMaxDiscount = isRewardMethod ? false : rewardAmount > maxRewardFromLimit;

  const handleProceedToForm = () => {
    if (!termsAccepted) return;
    setShowForm(true);
    setRewardAmountInput("");
    setUseRewardDiscount(false);
  };

  const handlePay = () => {
    setError("");

    let actualRewardUsed = 0;

    if (isRewardMethod) {
      if (!hasEnoughBalance) {
        setError(REWARD_MESSAGES.insufficientBalance(price, totalBalance));
        return;
      }
      actualRewardUsed = rewardAmount;
    } else if (useRewardDiscount && rewardAmount > 0) {
      if (rewardAmount > totalBalance) {
        setError(REWARD_MESSAGES.amountExceedsBalance(totalBalance));
        return;
      }
      if (rewardAmount > maxRewardFromLimit) {
        setError(REWARD_MESSAGES.amountExceedsMaxDiscount(maxRewardFromLimit));
        return;
      }
      actualRewardUsed = rewardAmount;
    }

    const validationError = !isRewardMethod ? validatePaymentDetails(selectedMethod, details) : null;
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    const transactionId = `TXN${Date.now()}`;
    const transactionDate = new Date().toLocaleString("en-IN");
    const paymentMethod = isRewardMethod ? "Reward Points" : selectedMethod;
    const actualPaidAmount = remainingToPay;

    setTimeout(() => {
      const updatedWallet = { ...wallet };
      const sessionId = `interview_${Date.now()}`;
      let remainingToDeduct = actualRewardUsed;
      const balanceBefore = redeemableBalance;

      if (redeemableBalance >= actualRewardUsed) {
        updatedWallet.redeemable.balance -= actualRewardUsed;
        updatedWallet.redeemable.transactions.unshift({
          id: sessionId,
          label: `AI Interview — ${module.name}`,
          amount: actualRewardUsed,
          type: "debit",
          date: new Date().toISOString(),
          note: actualRewardUsed > 0
            ? `Reward discount applied — ${isRewardMethod ? 'full reward payment' : 'partial discount on ' + selectedMethod + ' payment'}`
            : `Paid via ${selectedMethod}`,
          sessionId: sessionId,
          totalPrice: module.priceWithGST,
          rewardUsed: actualRewardUsed,
          actualPaid: actualPaidAmount,
          paymentMethod: isRewardMethod ? 'Reward' : selectedMethod,
          balanceBefore: balanceBefore,
          balanceAfter: balanceBefore - actualRewardUsed,
        });
      } else {
        const redeemableUsed = redeemableBalance;
        remainingToDeduct -= redeemableUsed;
        updatedWallet.redeemable.balance = 0;
        updatedWallet.redeemable.transactions.unshift({
          id: sessionId,
          label: `AI Interview — ${module.name}`,
          amount: actualRewardUsed,
          type: "debit",
          date: new Date().toISOString(),
          note: `Reward discount applied — ₹${redeemableUsed} from redeemable, ₹${remainingToDeduct} from non-redeemable`,
          sessionId: sessionId,
          totalPrice: module.priceWithGST,
          rewardUsed: actualRewardUsed,
          actualPaid: actualPaidAmount,
          redeemablePortion: redeemableUsed,
          nonRedeemablePortion: remainingToDeduct,
          balanceBefore: balanceBefore,
          balanceAfter: 0,
        });

        updatedWallet.nonRedeemable.balance -= remainingToDeduct;
      }

      saveWallet(userEmail, updatedWallet);

      setTransactionDetails({
        id: transactionId,
        date: transactionDate,
        module: module.name,
        price: module.price,
        priceWithGST: module.priceWithGST,
        gst: module.priceWithGST - module.price,
        rewardUsed: actualRewardUsed,
        actualPaid: actualPaidAmount,
        paymentMethod: paymentMethod,
        paymentDetails: { ...details }
      });
      setShowSuccessCard(true);
    }, 1500);
  };

  const handleDetailChange = (e) => {
    const { name, value } = e.target;
    setDetails((prev) => ({ ...prev, [name]: value }));
  };

  const downloadReceipt = async () => {
    if (!transactionDetails) return;
    try {
      const { jsPDF } = await import("jspdf");
      const html2canvas = (await import("html2canvas")).default;
      const toBase64 = async (url) => {
        const resp = await fetch(url);
        const blob = await resp.blob();
        return new Promise((r) => {
          const reader = new FileReader();
          reader.onloadend = () => r(reader.result);
          reader.readAsDataURL(blob);
        });
      };
      let logoSrc = null;
      try { logoSrc = await toBase64(receiptLogo); } catch { logoSrc = null; }

      const paymentD = transactionDetails?.paymentDetails || {};
      let upiId = paymentD.upiId || "";
      let bank = paymentD.bankName || "";
      let card = { number: paymentD.cardNum || "" };

      const item = {
        label: "AI Mock Interview",
        countryName: transactionDetails.module,
        price: transactionDetails.price,
        data: "1 Session",
        validity: 30,
        type: "practice",
      };
      const methodMap = { debit: "card", credit: "card", upi: "upi", netbanking: "netbanking", reward: "wallet" };
      const payMethod = methodMap[selectedMethod] || "card";
      const quantity = 1;

      const html = generateReceiptHTML({
        item, quantity, payMethod,
        billingDetails: { firstName: billingName.first, lastName: billingName.last, email: billingEmail, country: billingCountry, phone: billingPhone, phoneCode },
        orderId: transactionDetails.id,
        currency: "INR",
        appliedDiscount: transactionDetails.rewardUsed || 0,
        logoSrc, pdf: true, inr: true,
        upiId, bank, card, simType: ""
      });

      const container = document.createElement("div");
      container.innerHTML = html;
      container.style.position = "fixed";
      container.style.left = "-9999px";
      container.style.top = "0";
      container.style.width = "210mm";
      container.style.background = "#f5f9ff";
      document.body.appendChild(container);
      const el = container.querySelector(".Receipt-card") || container.firstElementChild;
      const canvas = await html2canvas(el, { scale: 2, useCORS: true, logging: false, backgroundColor: "#f5f9ff" });
      document.body.removeChild(container);

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const imgWidth = 210;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);
      pdf.save(`TheVisaCo-Receipt-${transactionDetails.id}.pdf`);
    } catch (err) {
      console.error("PDF generation failed:", err);
    }
  };

  const handleContinueAfterPayment = () => {
    setShowSuccessCard(false);
    onPaymentSuccess(module.id);
  };

  const remainingAfterReward = totalBalance - rewardAmount;

  const canUseRewardDiscount = totalBalance > 0;
  const rewardInputMax = isRewardMethod ? Math.min(price, totalBalance) : Math.min(maxRewardFromLimit, totalBalance);

  return (
    <>
    <div className="AIpay-overlay" onClick={onClose}>
      <div className="AIpay-box AIglass-strong" onClick={(e) => e.stopPropagation()}>
        <button className="AIpay-close-btn" onClick={onClose}>
          <X size={18} strokeWidth={2} />
        </button>

        <div className="AIpay-header">
          <div>
            <h3 className="AIpay-header__title">{PAYMENT_OVERLAY_CONTENT.title}</h3>
            <p className="AIpay-header__sub">{module.name} — {module.label}</p>
          </div>
        </div>

        <div className="AIpay-price-row">
          <span className="AIpay-price__currency">₹</span>
          <span className="AIpay-price__amount" style={{ color: module.color }}>{module.priceWithGST}</span>
          <span className="AIpay-price__period AIgst-label">incl GST {module.gstPercent}%</span>
        </div>

        {session && (
          <div className="AIpay-price-breakdown">
            <div className="AIpay-price-breakdown-row">
              <span>{PRICE_BREAKDOWN_LABELS.basePrice}</span>
              <span className="AIpay-price-breakdown-value">₹{module.price.toLocaleString("en-IN")}</span>
            </div>
            <div className="AIpay-price-breakdown-row">
              <span>{PRICE_BREAKDOWN_LABELS.gst(module.gstPercent)}</span>
              <span className="AIpay-price-breakdown-value">₹{(module.priceWithGST - module.price).toLocaleString("en-IN")}</span>
            </div>
            <div className="AIpay-price-breakdown-row">
              <span>{PRICE_BREAKDOWN_LABELS.convenienceFee}</span>
              <span className="AIpay-price-breakdown-value AIpay-price-breakdown-value--free">{PRICE_BREAKDOWN_LABELS.free}</span>
            </div>

            {rewardAmount > 0 && (
              <>
                <div className="AIpay-price-breakdown-divider" />
                <div className="AIpay-price-breakdown-row AIpay-price-breakdown-row--discount">
                  <span>{REWARD_MESSAGES.rewardAppliedLine} {isRewardMethod ? "" : `(₹${maxRewardFromLimit} max on base)`}</span>
                  <span className="AIpay-price-breakdown-value AIpay-price-breakdown-value--discount">−₹{rewardAmount.toLocaleString("en-IN")}</span>
                </div>
              </>
            )}

            <div className="AIpay-price-breakdown-divider" />
            <div className="AIpay-price-breakdown-row AIpay-price-breakdown-row--total">
              <span>{rewardAmount > 0 ? PRICE_BREAKDOWN_LABELS.subtotal : PRICE_BREAKDOWN_LABELS.total}</span>
              <span className="AIpay-price-breakdown-value">₹{remainingToPay.toLocaleString("en-IN")}</span>
            </div>
          </div>
        )}

        <ul className="AIpay-features-list">
          {module.features.map((f, i) => (
            <li key={i} className="AIpay-features-item">
              <Check size={14} strokeWidth={2.5} color={module.color} />
              <span>{f}</span>
            </li>
          ))}
        </ul>

        {!showForm ? (
          <>
            <label className="AIpay-terms-label" onClick={() => setTermsAccepted((v) => !v)}>
              <div className={`AIpay-terms-checkbox ${termsAccepted ? "AIpay-terms-checkbox--checked" : ""}`} style={{ "--pay-check-color": module.color }}>
                {termsAccepted && <Check size={12} strokeWidth={3} color="#fff" />}
              </div>
              <span className="AIpay-terms-text">
                {PAYMENT_OVERLAY_CONTENT.termsText}{" "}
                <span className="AIpay-terms-link" onClick={(e) => { e.stopPropagation(); setShowTerms(true); }}>
                  View Terms & Conditions
                </span>
              </span>
            </label>

            <button
              className={`AIbtn AIbtn-full AIpay-btn ${termsAccepted ? "" : "AIbtn--disabled"}`}
              style={{ "--pay-btn-color": module.color }}
              onClick={termsAccepted ? handleProceedToForm : undefined}
              disabled={!termsAccepted}
            >
              <CreditCard size={15} strokeWidth={2} />
              Proceed to Pay — ₹{module.priceWithGST}
            </button>
          </>
        ) : (
          <>
            <div className="AIpay-billing-section">
              <h4 className="AIpay-section-title">Billing Details</h4>
              <div className="AIpay-billing-row">
                <div className="AIpay-billing-field">
                  <label className="AIpay-field-label">First Name</label>
                  <input className="AIpay-billing-input" type="text" placeholder="John" value={billingName.first} onChange={e => setBillingName(p => ({ ...p, first: e.target.value }))} />
                </div>
                <div className="AIpay-billing-field">
                  <label className="AIpay-field-label">Last Name</label>
                  <input className="AIpay-billing-input" type="text" placeholder="Doe" value={billingName.last} onChange={e => setBillingName(p => ({ ...p, last: e.target.value }))} />
                </div>
              </div>
              <div className="AIpay-billing-row">
                <div className="AIpay-billing-field">
                  <label className="AIpay-field-label">Email ID</label>
                  <input className="AIpay-billing-input" type="email" placeholder="john@email.com" value={billingEmail} onChange={e => setBillingEmail(e.target.value)} />
                </div>
                <div className="AIpay-billing-field">
                  <label className="AIpay-field-label">Country</label>
                  <input className="AIpay-billing-input" type="text" placeholder="e.g. India" value={billingCountry} onChange={e => setBillingCountry(e.target.value)} />
                </div>
              </div>
              <div className="AIpay-billing-row">
                <div className="AIpay-billing-field">
                  <label className="AIpay-field-label">Phone Code</label>
                  <select className="AIpay-billing-input AIpay-billing-select" value={phoneCode} onChange={e => setPhoneCode(e.target.value)}>
                    {PHONE_CODE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div className="AIpay-billing-field">
                  <label className="AIpay-field-label">Phone Number</label>
                  <input className="AIpay-billing-input" type="tel" placeholder="98765 43210" value={billingPhone} onChange={e => setBillingPhone(e.target.value)} />
                </div>
              </div>
            </div>
            <PaymentForm
              selectedMethod={selectedMethod}
              onMethodSelect={(method) => {
                setSelectedMethod(method);
                setRewardAmountInput("");
                setUseRewardDiscount(false);
              }}
              details={details}
              onDetailChange={handleDetailChange}
              walletBalances={{ redeemable: roundedRedeemable, nonRedeemable: roundedNonRedeemable }}
              rewardLocked={!hasEnoughBalance}
            />

            {isRewardMethod && (
              hasEnoughBalance ? (
              <div className="AIpay-reward-input-section">
                <label className="AIpay-reward-input-label">
                  {REWARD_LABELS.walletSectionTitle}
                </label>
                <div className="AIpay-reward-input-row AIpay-reward-input-row--static">
                  <span className="AIpay-reward-currency">{REWARD_LABELS.currencySymbol}</span>
                  <span className="AIpay-reward-static-amount">{price.toLocaleString("en-IN")}</span>
                </div>

                <div className="AIpay-reward-remaining">
                  <span>{REWARD_MESSAGES.remainingBalance(roundedTotal - price)}</span>
                </div>
              </div>
              ) : (
              <div className="AIpay-reward-locked-section">
                <div className="AIpay-reward-locked-icon">
                  <Lock size={24} strokeWidth={1.5} />
                </div>
                <h4 className="AIpay-reward-locked-title">{REWARD_LABELS.lockedBadge}</h4>
                <p className="AIpay-reward-locked-desc">
                  Your wallet balance (₹{totalBalance.toLocaleString("en-IN")}) is less than the price (₹{price.toLocaleString("en-IN")}). Add more rewards to unlock full reward payment.
                </p>
                <div className="AIpay-reward-locked-progress">
                  <div className="AIpay-reward-locked-progress-bar" style={{ width: `${Math.min((totalBalance / price) * 100, 100)}%` }} />
                </div>
                <span className="AIpay-reward-locked-progress-label">{Math.floor((totalBalance / price) * 100)}% of price covered</span>
              </div>
              )
            )}

            {!isRewardMethod && selectedMethod && (
              session && canUseRewardDiscount ? (
              <div className="AIpay-reward-discount-section">
                <div
                  className="AIpay-reward-discount-toggle"
                  onClick={() => {
                    setUseRewardDiscount(!useRewardDiscount);
                    setRewardAmountInput("");
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { setUseRewardDiscount(!useRewardDiscount); setRewardAmountInput(""); } }}
                >
                  <div className={`AIpay-reward-discount-circle ${useRewardDiscount ? "AIactive" : ""}`} />
                  <span className="AIpay-reward-discount-label">
                    {REWARD_LABELS.toggleLabel}
                    <span className={`AIpay-reward-discount-badge ${useRewardDiscount ? "" : "AIpay-reward-discount-badge--inactive"}`}>
                      {roundedTotal > 0 ? `${REWARD_LABELS.toggleAvailable}: ₹${roundedTotal}` : REWARD_LABELS.toggleUnavailable}
                    </span>
                  </span>
                </div>

                {useRewardDiscount && (
                  <div className="AIpay-reward-discount-body">
                    <div className="AIpay-reward-input-row">
                      <span className="AIpay-reward-currency">{REWARD_LABELS.currencySymbol}</span>
                      <input
                        className="AIpay-reward-input"
                        type="text"
                        inputMode="numeric"
                        placeholder={`Max ₹${maxRewardAllowed}`}
                        value={rewardAmountInput}
                        onChange={(e) => setRewardAmountInput(e.target.value.replace(/\D/g, ""))}
                      />
                    </div>

                    {rewardAmount > 0 && (
                      <div className={`AIpay-balance-preview ${rewardExceedsBalance || rewardExceedsMaxDiscount ? "AIpay-balance-preview--insufficient" : ""}`}>
                        {rewardExceedsBalance ? (
                          <>
                            <X size={14} color="#dc2626" strokeWidth={2} />
                            <span>{REWARD_MESSAGES.amountExceedsBalance(totalBalance)}</span>
                          </>
                        ) : rewardExceedsMaxDiscount ? (
                          <>
                            <X size={14} color="#dc2626" strokeWidth={2} />
                            <span>{REWARD_MESSAGES.amountExceedsMaxDiscount(maxRewardFromLimit)}</span>
                          </>
                        ) : (
                          <>
                            <Check size={14} color="#16a34a" strokeWidth={2} />
                            <span>{REWARD_MESSAGES.rewardBreakdown(rewardAmount, remainingToPay)}</span>
                          </>
                        )}
                      </div>
                    )}

                    {!rewardExceedsBalance && !rewardExceedsMaxDiscount && rewardAmount > 0 && (
                      <div className="AIpay-reward-remaining">
                        <span>{REWARD_MESSAGES.remainingBalance(remainingAfterReward)}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
              ) : (
              <div className="AIpay-reward-locked-section" onClick={() => setShowLoginGate(true)} style={{ cursor: "pointer" }}>
                <div className="AIpay-reward-locked-icon">
                  <Lock size={24} strokeWidth={1.5} />
                </div>
                <h4 className="AIpay-reward-locked-title">Sign-In Required For The Reward Discount</h4>
              </div>
              )
            )}

            {error && <div className="AIpay-error">{error}</div>}

            <button
              className={`AIbtn AIbtn-full AIpay-btn ${selectedMethod && termsAccepted && !loading ? "" : "AIbtn--disabled"}`}
              style={{ "--pay-btn-color": module.color }}
              onClick={selectedMethod && termsAccepted && !loading ? handlePay : undefined}
              disabled={!selectedMethod || !termsAccepted || loading}
            >
              {loading ? (
                <span className="AIpay-btn__spinner" />
              ) : isRewardMethod ? (
                <>
                  <CreditCard size={15} strokeWidth={2} />
                  {REWARD_MESSAGES.payWithRewards(rewardAmount)}
                </>
              ) : (
                <>
                  <CreditCard size={15} strokeWidth={2} />
                  {rewardAmount > 0 ? (
                    `Pay ₹${remainingToPay.toLocaleString("en-IN")} (₹${rewardAmount.toLocaleString("en-IN")} via rewards)`
                  ) : (
                    `${PAYMENT_OVERLAY_CONTENT.buttonLabel} — ₹${remainingToPay.toLocaleString("en-IN")}`
                  )}
                </>
              )}
            </button>
          </>
        )}

        <p className="AIpay-secure-note">
          <ShieldCheck size={12} color="rgba(13,27,62,0.35)" />
          {PAYMENT_OVERLAY_CONTENT.secureNote}
        </p>
      </div>
    
      {showSuccessCard && (
        <div className="AIpay-success-card">
          <div className="AIpay-success-icon">
            <Check size={48} strokeWidth={2} color="#16a34a" />
          </div>
          <h3 className="AIpay-success-title">Payment Successful!</h3>
          <p className="AIpay-success-sub">Your payment has been processed successfully</p>
          <div className="AIpay-success-details">
            <div className="AIpay-success-row">
              <span>Transaction ID</span>
              <span>{transactionDetails?.id}</span>
            </div>
            <div className="AIpay-success-row">
              <span>Amount Paid</span>
              <span>Rs {(transactionDetails?.actualPaid ?? transactionDetails?.priceWithGST)?.toLocaleString("en-IN")}</span>
            </div>
          </div>
          <div className="AIpay-success-actions">
            <button className="AIbtn AIbtn-full AIpay-btn" onClick={downloadReceipt}>
              <Download size={15} strokeWidth={2} />
              Download Receipt
            </button>
            <button className="AIbtn AIbtn-full AIpay-btn" onClick={handleContinueAfterPayment}>
              <ArrowRight size={15} strokeWidth={2} />
              Continue
            </button>
          </div>
        </div>
      )}
    </div>
    {showTerms && <TermsModal onClose={() => setShowTerms(false)} />}
    <LoginGate open={showLoginGate} onClose={() => setShowLoginGate(false)} onLoginSuccess={() => setShowLoginGate(false)} />
  </>
  );
}

// ─── RESULTS SCREEN ───────────────────────────────────────────────────────────
function ResultsScreen({ selectedModule, selectedVoice, answers, aiResult, onRetake, onHome, activeQuestions, attemptsUsed, onOpenPayment }) {
  const mod = MODULE_CARDS.find((m) => m.id === selectedModule);
  const remaining = mod.maxAttempts - attemptsUsed;
  const hasAttemptsLeft = remaining > 0;
  const answerListRef = useRef(null);
  useAOS();

  const scores = aiResult?.scores || {};
  const evaluations = aiResult?.evaluations || [];

  const overall =
    scores && Object.values(scores).length > 0
      ? Math.round(Object.values(scores).reduce((a, b) => a + b, 0) / Object.values(scores).length)
      : Math.round(
          evaluations.reduce((sum, e) => sum + (e.score || 0), 0) / Math.max(evaluations.length, 1)
        );

  const pillClass = overall >= 80 ? "AIgood" : overall >= 65 ? "AIaverage" : "AIpoor";
  const pillLabel =
    overall >= 80 ? "Likely to be Approved" : overall >= 65 ? "Needs Improvement" : "Requires Significant Work";
  const pillEmoji = overall >= 80 ? "🟢" : overall >= 65 ? "🟡" : "🔴";

  const getScoreClass = (score) => (score >= 75 ? "AIgood" : score >= 55 ? "AIaverage" : "AIpoor");

  return (
    <div className="AIpage">
      <div className="AIorb AIorb-1" />
      <div className="AIorb AIorb-2" />

      <div className="AIresults-wrapper">
        <div data-aos="fade-up" className="AIresults-header">
          <h2 className="AIresults-header__h2">Interview Complete!</h2>
          <p className="AIresults-header__sub">
            Here is your {mod?.label} module performance analysis — {voiceConfig[selectedVoice].name} has assessed your responses
          </p>
        </div>

        <div className="AIresults-top-grid">
          <div className="AIresults-score-row">
            <div data-aos="zoom-in" className="AIoverall-score-card AIglass-strong">
              <div className={`AIoverall-score-card__pill ${pillClass}`}>
                <span>{pillEmoji}</span>
                <span className={`AIoverall-score-card__pill-text ${pillClass}`}>{pillLabel}</span>
              </div>
              <div className="AIoverall-score-card__number">
                {overall}<span>%</span>
              </div>
              <div className="AIoverall-score-card__label">Overall Interview Score</div>
            </div>

            <div data-aos="fade-up" data-aos-delay="100" className="AIscore-breakdown-card AIglass-strong">
              <h3 className="AIscore-breakdown-card__title">Performance Breakdown</h3>
              <div className="AIscore-rings-grid">
                {scoreCategories.map((cat) => (
                  <ScoreRing key={cat.key} score={scores[cat.key] ?? 0} label={cat.label} color={cat.color} />
                ))}
              </div>
            </div>
          </div>

        <div className="AIresults-right-col">
          <div data-aos="fade-up" data-aos-delay="200" className="AIanswer-review-card AIglass-strong">
            <div className="AIanswer-review-card__header">
              <h3 className="AIanswer-review-card__title">Your Answers & AI Evaluation</h3>
              <div className="AIanswer-review-card__scroll-btns">
                <button className="AIanswer-scroll-btn AIanswer-scroll-btn--left" onClick={() => answerListRef.current?.scrollBy({ left: -150, behavior: 'smooth' })}>
                  <ArrowLeft size={16} />
                </button>
                <button className="AIanswer-scroll-btn AIanswer-scroll-btn--right" onClick={() => answerListRef.current?.scrollBy({ left: 150, behavior: 'smooth' })}>
                  <ArrowLeft size={16} style={{ transform: 'rotate(180deg)' }} />
                </button>
              </div>
            </div>
            <div className="AIanswer-list" ref={answerListRef}>
            {activeQuestions.map((q, i) => {
              const evalData = evaluations.find((e) => e.questionId === q.id);
              const rawAnswer = answers[i]?.answer;
              const isNoAnswer =
                !rawAnswer ||
                rawAnswer.trim() === "" ||
                rawAnswer === NO_ANSWER_PROVIDED ||
                rawAnswer === NO_ANSWER_RECORDED;
              const answerDisplay = isNoAnswer ? NO_ANSWER_WAS_PROVIDED : rawAnswer;
              const score = evalData?.score ?? null;
              const scoreClass = score !== null ? getScoreClass(score) : "AIaverage";

              return (
                <div key={q.id} className="AIanswer-item">
                  <div className="AIanswer-item__inner">
                    <div className="AIanswer-item__header">
                      <div className="AIanswer-item__num">{q.id}</div>
                      <div className="AIanswer-item__cat">{q.category}</div>
                      <div className="AIanswer-item__question">{q.question}</div>
                    </div>
                    <div className="AIanswer-item__content">
                      <div>
                        <div className="AIanswer-item__section-label">Your Answer</div>
                        {isNoAnswer ? (
                          <div className="AIanswer-item__answer AIanswer-item__answer--no-answer">
                            {NO_ANSWER_WAS_PROVIDED}
                          </div>
                        ) : (
                          <div className="AIanswer-item__answer AIanswer-item__answer--answered">
                            {answerDisplay}
                          </div>
                        )}
                      </div>

                      {evalData && (
                        <div className="AIanswer-item__eval">
                          <div className="AIanswer-item__eval-header">
                            <span className="AIanswer-item__eval-label">AI Evaluation</span>
                            {score !== null && (
                              <span className={`AIanswer-item__eval-score ${scoreClass}`}>
                                {score}/100
                              </span>
                            )}
                          </div>

                          {evalData.idealAnswer && (
                            <div className="AIanswer-item__eval-ideal">
                              <strong>Ideal answer:</strong> {evalData.idealAnswer}
                            </div>
                          )}

                          {evalData.remark && (
                            <div className="AIanswer-item__eval-remark">"{evalData.remark}"</div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div data-aos="fade-up" data-aos-delay="300" className="AItips-card AIglass-strong">
          <h3 className="AItips-card__title">💡 Key Improvement Tips</h3>
          <div className="AItips-list">
            {improvementTips.map((item, i) => (
              <div key={i} className="AItip-item">
                <span className="AItip-item__check">✓</span>
                <div>
                  <div className="AItip-item__title">{item.tip}</div>
                  <div className="AItip-item__detail">{item.detail}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>

      <div className="AIresults-cta">
          {hasAttemptsLeft ? (
            <button className="AIbtn AIbtn-primary" onClick={onRetake}>
              🔄 Retake Interview ({remaining} attempt{remaining !== 1 ? "s" : ""} left)
            </button>
          ) : selectedModule === "easy" ? (
            <div className="AIresults-upgrade-btns">
              <button className="AIbtn AIbtn--renew-module" style={{ "--module-btn-color": "#0b73b8" }} onClick={() => onOpenPayment("medium")}>
                Buy Medium Module
              </button>
              <button className="AIbtn AIbtn--renew-module" style={{ "--module-btn-color": "#891515" }} onClick={() => onOpenPayment("pro")}>
                Buy Pro Module
              </button>
            </div>
          ) : (
            <button className="AIbtn AIbtn--renew-module" style={{ "--module-btn-color": mod.color }} onClick={() => onOpenPayment(selectedModule)}>
              Renew {mod.name} Module
            </button>
          )}
        <button className="AIbtn AIbtn-ghost AIbtn--back-home" onClick={onHome}>
           Back to Home
        </button>
        </div>
      </div>
    </div>
  );
}

// ─── ROOT APP ─────────────────────────────────────────────────────────────────
export default function USMockInterview() {
  const [screen, setScreen] = useState("landing");
  useEffect(() => {
    configService.getConfig().then(r => {
      const qs = r.data?.interviewQuestions || [];
      if (qs.length) {
        const grouped = { easy: {}, medium: {}, difficult: {} };
        for (const q of qs) {
          if (!grouped[q.module_id]) grouped[q.module_id] = {};
          if (!grouped[q.module_id][q.set_id]) grouped[q.module_id][q.set_id] = [];
          grouped[q.module_id][q.set_id].push({ id: q.id, category: q.category, question: q.question, hint: q.hint });
        }
        interviewQuestions = grouped.easy?.set1 || interviewQuestions;
      }
    }).catch(() => {});
  }, []);
  const [selectedModule, setSelectedModule] = useState("easy");
  const [selectedSet, setSelectedSet] = useState("set1");
  const [selectedVoice, setSelectedVoice] = useState("female");
  const [attemptsUsed, setAttemptsUsed] = useState(0);
  const [payForModuleId, setPayForModuleId] = useState(null);
  const [currentQ, setCurrentQ] = useState(0);
  const [isAISpeaking, setIsAISpeaking] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [answers, setAnswers] = useState([]);
  const [timer, setTimer] = useState(TIMER_DURATION);
  const [questionPhase, setQuestionPhase] = useState("idle");
  const [aiResult, setAiResult] = useState(null);

  const mod = MODULE_CARDS.find((m) => m.id === selectedModule);
  const activeQuestions = getQuestionsByModule(selectedModule, selectedSet);

  const synthRef = useRef(window.speechSynthesis);
  const recognitionRef = useRef(null);
  const timerRef = useRef(null);
  const transcriptRef = useRef("");
  // Accumulate transcript segments so pausing/resuming doesn't wipe previous speech
  const accumulatedTranscriptRef = useRef("");

  useEffect(() => {
    transcriptRef.current = transcript;
  }, [transcript]);

  // Setup Speech Recognition once
  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SR) {
      const recog = new SR();
      recog.continuous = true;
      recog.interimResults = true;
      recog.lang = "en-US";

      recog.onresult = (e) => {
        // Build the current recognition session's interim/final text
        let sessionText = "";
        for (let i = e.resultIndex; i < e.results.length; i++) {
          sessionText += e.results[i][0].transcript;
        }
        // Combine the already-accumulated text from previous recognition segments
        // with the current session's live text (avoid duplicating finalized text)
        let finalizedThisSession = "";
        for (let i = e.resultIndex; i < e.results.length; i++) {
          if (e.results[i].isFinal) {
            finalizedThisSession += e.results[i][0].transcript;
          }
        }

        // Update the accumulated ref when we get finalized results
        if (finalizedThisSession) {
          accumulatedTranscriptRef.current =
            (accumulatedTranscriptRef.current + " " + finalizedThisSession).trim();
        }

        // Display = accumulated finalized + current interim
        const interimText = Array.from(e.results)
          .slice(e.resultIndex)
          .filter((r) => !r.isFinal)
          .map((r) => r[0].transcript)
          .join("");

        const display = (accumulatedTranscriptRef.current + " " + interimText).trim();
        setTranscript(display);
        transcriptRef.current = display;
      };

      recog.onend = () => {
        setIsListening(false);
        // If still in listening phase (paused mid-answer), restart recognition so
        // the user can resume speaking — transcript is preserved via accumulatedTranscriptRef
        // We only auto-restart if questionPhase is "listening" AND timer > 0
        // We use a small flag pattern via the ref
      };

      recog.onerror = (e) => {
        if (e.error === "no-speech" || e.error === "audio-capture") {
          // Non-fatal; recognition stopped — will be restarted if still listening
        }
      };

      recognitionRef.current = recog;
    }
    return () => {
      synthRef.current.cancel();
      clearInterval(timerRef.current);
    };
  }, []);

  const speak = useCallback(
    (text, onEnd) => {
      synthRef.current.cancel();
      const utter = new SpeechSynthesisUtterance(text);
      const voices = synthRef.current.getVoices();
      const cfg = voiceConfig[selectedVoice];
      const preferred = cfg.preferredVoiceNames;
      const match =
        voices.find((v) => preferred.some((p) => v.name.toLowerCase().includes(p))) ||
        voices.find((v) => v.lang.startsWith("en-US")) ||
        voices[0];

      if (match) utter.voice = match;
      utter.pitch = cfg.pitch;
      utter.rate = cfg.rate;
      utter.volume = 1;
      utter.onstart = () => setIsAISpeaking(true);
      utter.onend = () => {
        setIsAISpeaking(false);
        if (onEnd) onEnd();
      };
      synthRef.current.speak(utter);
    },
    [selectedVoice]
  );

  const finishInterview = useCallback(async (finalAnswers) => {
    setScreen("analyzing");
    try {
      const result = await evaluateAnswersWithAI(finalAnswers, activeQuestions);
      setAiResult(result);
    } catch (err) {
      console.error("AI evaluation failed:", err);
      setAiResult({
        evaluations: finalAnswers.map((a) => {
          const isNoAnswer =
            !a.answer ||
            a.answer.trim() === "" ||
            NO_ANSWER_MARKERS.some((marker) => a.answer.toLowerCase().includes(marker));
          return {
            questionId: a.questionId,
            idealAnswer: "A clear, concise, and honest answer demonstrating strong ties to home country.",
            score: isNoAnswer ? 0 : Math.floor(Math.random() * 30 + 60),
            remark: isNoAnswer
              ? "No answer was provided. This results in zero marks for this question."
              : "Answer recorded. AI evaluation unavailable — please retry.",
          };
        }),
        scores: {
          clarity: 0,
          confidence: 0,
          consistency: 0,
          relevance: 0,
        },
      });
    }
    setScreen("results");

    setAttemptsUsed((prev) => {
      const newCount = prev + 1;
      if (newCount >= mod.maxAttempts) {
        setTimeout(() => setPayForModuleId(selectedModule), 500);
      }
      return newCount;
    });
  }, [activeQuestions, mod]);

  const askQuestion = useCallback(
    (index, currentAnswers) => {
      if (index >= activeQuestions.length) {

        setQuestionPhase("done");
        speak(
          "Thank you for your time. Your interview has been completed. Please wait while we analyze your responses.",
          () => setTimeout(() => finishInterview(currentAnswers), 500)
        );
        return;
      }
      setCurrentQ(index);
      setTranscript("");
      transcriptRef.current = "";
      accumulatedTranscriptRef.current = "";
      setQuestionPhase("speaking");
      setTimer(TIMER_DURATION);

      speak(activeQuestions[index].question, () => {
        setQuestionPhase("listening");
        setIsListening(true);
        if (recognitionRef.current) {
          try { recognitionRef.current.start(); } catch (_) {}
        }

        timerRef.current = setInterval(() => {
          setTimer((t) => {
            if (t <= 1) {
              clearInterval(timerRef.current);
              const savedAnswer =
                transcriptRef.current.trim() ||
                accumulatedTranscriptRef.current.trim() ||
                NO_ANSWER_RECORDED;
              const newAnswers = [
                ...currentAnswers,
                { questionId: activeQuestions[index].id, answer: savedAnswer },
              ];
              setAnswers(newAnswers);
              setIsListening(false);
              if (recognitionRef.current) {
                try { recognitionRef.current.stop(); } catch (_) {}
              }
              setQuestionPhase("idle");
              setTimeout(() => askQuestion(index + 1, newAnswers), 1200);
              return 0;
            }
            return t - 1;
          });
        }, 1000);
      });
    },
    [speak, finishInterview, activeQuestions]
  );

  const handleInterviewBack = useCallback(() => {
    synthRef.current.cancel();
    clearInterval(timerRef.current);
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch (_) {}
    }
    setIsAISpeaking(false);
    setIsListening(false);
    setQuestionPhase("idle");
    setScreen("setup");
  }, []);

  const handleNext = useCallback(() => {
    clearInterval(timerRef.current);
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch (_) {}
    }
    setIsListening(false);
    setQuestionPhase("idle");
    const savedAnswer =
      transcriptRef.current.trim() ||
      accumulatedTranscriptRef.current.trim() ||
      NO_ANSWER_RECORDED;
    setAnswers((prev) => {
      const newAnswers = [
        ...prev,
        { questionId: activeQuestions[currentQ].id, answer: savedAnswer },
      ];
      setTimeout(() => askQuestion(currentQ + 1, newAnswers), 1200);
      return newAnswers;
    });
  }, [currentQ, askQuestion, activeQuestions]);

  const startInterview = useCallback(() => {
    setScreen("interview");
    setCurrentQ(0);
    setAnswers([]);
    setAiResult(null);
    setTranscript("");
    transcriptRef.current = "";
    accumulatedTranscriptRef.current = "";
    setTimeout(() => {
      const hour = new Date().getHours();
      const greeting = hour < 12 ? INTERVIEW_GREETINGS.morning : INTERVIEW_GREETINGS.afternoon;
      const cfg = voiceConfig[selectedVoice];
      const intro = buildIntroSpeech(greeting, cfg.name, cfg.badge);
      speak(intro, () => setTimeout(() => askQuestion(0, []), 800));
    }, 500);
  }, [selectedVoice, speak, askQuestion]);

  const handleRetake = () => {
    synthRef.current.cancel();
    const remaining = mod.maxAttempts - attemptsUsed;
    if (remaining <= 0) {
      setPayForModuleId(selectedModule);
    } else {
      setScreen("setup");
    }
  };

  const handlePaymentSuccess = (moduleId) => {
    setPayForModuleId(null);
    setSelectedModule(moduleId);
    setSelectedSet("set1");
    setAttemptsUsed(0);
    setAiResult(null);
    setAnswers([]);
    setScreen("setup");
  };

  const handleOpenPayment = (moduleId) => {
    setPayForModuleId(moduleId);
  };

  const handleModuleSelect = (moduleId) => {
    setSelectedModule(moduleId);
  };

  const handleContinue = () => {
    if (!selectedModule) return;
    const mod = MODULE_CARDS.find((m) => m.id === selectedModule);
    if (mod.price > 0) {
      setPayForModuleId(selectedModule);
    } else {
      setSelectedSet("set1");
      setAttemptsUsed(0);
      setAiResult(null);
      setAnswers([]);
      setScreen("setup");
    }
  };

  const handleHome = () => {
    synthRef.current.cancel();
    setScreen("landing");
  };

  // ─── SCREEN ROUTING ──────────────────────────────────────────────────────
  if (screen === "landing") return <LandingScreen onStart={() => setScreen("modules")} />;
  if (screen === "modules")
    return (
      <>
        <ModuleSelectionScreen
          selectedModule={selectedModule}
          onSelectModule={handleModuleSelect}
          onBack={() => setScreen("landing")}
          onContinue={handleContinue}
          onHome={() => setScreen("landing")}
        />
        {payForModuleId && screen === "modules" && (
          <PaymentOverlay
            module={MODULE_CARDS.find((m) => m.id === payForModuleId)}
            onPaymentSuccess={handlePaymentSuccess}
            onClose={() => setPayForModuleId(null)}
          />
        )}
      </>
    );
  if (screen === "setup")
    return (
      <SetupScreen
        selectedModule={selectedModule}
        attemptsUsed={attemptsUsed}
        selectedSet={selectedSet}
        onSelectSet={setSelectedSet}
        selectedVoice={selectedVoice}
        onSelectVoice={setSelectedVoice}
        onBack={() => setScreen("modules")}
        onBegin={startInterview}
      />
    );
  if (screen === "interview")
    return (
      <InterviewScreen
        selectedModule={selectedModule}
        selectedVoice={selectedVoice}
        currentQ={currentQ}
        isAISpeaking={isAISpeaking}
        isListening={isListening}
        transcript={transcript}
        timer={timer}
        questionPhase={questionPhase}
        onNext={handleNext}
        onBack={handleInterviewBack}
        activeQuestions={activeQuestions}
      />
    );
  if (screen === "analyzing") return <AnalyzingScreen />;
  if (screen === "results")
    return (
      <>
        <ResultsScreen
          selectedModule={selectedModule}
          selectedVoice={selectedVoice}
          answers={answers}
          aiResult={aiResult}
          onRetake={handleRetake}
          onHome={handleHome}
          activeQuestions={activeQuestions}
          attemptsUsed={attemptsUsed}
          onOpenPayment={handleOpenPayment}
        />
        {payForModuleId && (
          <PaymentOverlay
            module={MODULE_CARDS.find((m) => m.id === payForModuleId)}
            onPaymentSuccess={handlePaymentSuccess}
            onClose={() => setPayForModuleId(null)}
          />
        )}
      </>
    );

  return null;
}